// Feed — the conversation. User messages are right-aligned bubbles; the agent's reply is
// full-width prose with its tool use folded into a "steps" timeline. The scroller follows
// new output only while the reader is at the bottom; scrolling up parks it and shows
// "Jump to latest".

import { h, Component, Fragment } from 'preact';
import { Rich, rawUrl, copyText } from './rich.js';
import { Orb, PhaseLine } from './orb.js';
import { Icon, KIND_ICON, fileIcon } from './icons.js';
import { toTurns, toSegments, stepsDuration, fmtDuration, fmtSize } from './transcript.js';

// ─── one step (a tool call or a stretch of reasoning) ─────────────────────────
function StepBody({ it, pid, onOpenFile }) {
  const parts = [];
  if (it.command) parts.push(h('pre', { key: 'cmd', className: 'step-cmd' }, h('span', { className: 'prompt' }, '$ '), it.command));
  if (it.diff) {
    parts.push(h('div', { key: 'diff', className: 'step-diff' },
      it.diff.old ? h('pre', { className: 'del' }, it.diff.old) : null,
      it.diff.new ? h('pre', { className: 'add' }, it.diff.new) : null));
  }
  if (it.preview && !it.diff) parts.push(h('pre', { key: 'pv', className: 'step-out' }, it.preview));
  if (it.output) parts.push(h('pre', { key: 'out', className: 'step-out' }, it.output));
  if (it.url) parts.push(h('a', { key: 'url', href: it.url, target: '_blank', rel: 'noopener noreferrer', className: 'step-link' }, it.url));
  if (it.path && pid && (it.kind === 'file_write' || it.kind === 'file_edit' || it.kind === 'file_read') && !it.path.startsWith('/')) {
    parts.push(h('button', { key: 'open', type: 'button', className: 'step-open', onClick: () => onOpenFile && onOpenFile(it.path) },
      h(Icon, { name: fileIcon(it.path) }), ' Open ', it.path));
  }
  return parts.length ? h('div', { className: 'step-body' }, parts) : null;
}

function stepLabel(it) {
  if (it.role === 'reasoning') return null;
  const done = it.status && it.status !== 'running' && it.status !== 'pending';
  return (done && it.settled) || it.label || it.short || it.tool_name || 'Working';
}

function StepRow({ it, pid, live, onOpenFile }) {
  if (it.role === 'assistant') {
    return h('li', { className: 'step step-say' }, h(Rich, { source: it.content, pid, onOpenFile }));
  }
  if (it.role === 'reasoning') {
    const ms = (it.t1 || it.ts) - it.ts;
    const thinking = live && !it.t1done;
    return h('li', { className: 'step step-thought' },
      h('details', null,
        h('summary', null, h(Icon, { name: 'thought' }),
          h('span', { className: 'step-label' }, ms > 1500 ? `Thought for ${fmtDuration(ms)}` : 'Thought it through')),
        h('div', { className: 'reason-text' }, it.content)));
  }
  const status = it.status || 'running';
  const running = status === 'running' || status === 'pending';
  const failed = status === 'failed' || status === 'error';
  const body = h(StepBody, { it, pid, onOpenFile });
  const head = [
    h(Icon, { name: KIND_ICON[it.kind] || 'tool' }),
    h('span', { className: 'step-label' }, stepLabel(it)),
    h('span', { className: 'step-meta' },
      it.elapsed_ms != null && !running && it.elapsed_ms > 900 ? fmtDuration(it.elapsed_ms) : null,
      running ? h('span', { className: 'spin', 'aria-label': 'running' })
        : failed ? h(Icon, { name: 'x', className: 'bad' }) : h(Icon, { name: 'check', className: 'good' })),
  ];
  return h('li', { className: 'step', 'data-status': status },
    body ? h('details', null, h('summary', null, head), body) : h('div', { className: 'step-head' }, head));
}

function StepsGroup({ items, pid, live, onOpenFile }) {
  const tools = items.filter((i) => i.role === 'tool');
  if (!tools.length && items.every((i) => i.role === 'reasoning')) {
    // just thinking: one block, the thought inside, no nested disclosure
    const ms = stepsDuration(items);
    return h('details', { className: 'steps thought' + (live ? ' live' : ''), open: live },
      h('summary', null, live ? h(Orb, { state: 'thinking', size: 16 }) : h(Icon, { name: 'thought' }),
        h('span', { className: 'steps-summary' + (live ? ' shimmer' : '') },
          live ? 'Thinking' : ms > 1500 ? `Thought for ${fmtDuration(ms)}` : 'Thought it through')),
      h('div', { className: 'reason-text solo' }, items.map((i) => i.content).join('\n\n')));
  }
  const running = live && items.some((i) => i.role === 'tool' && (i.status === 'running' || i.status === 'pending'));
  const current = running ? [...tools].reverse().find((i) => i.status === 'running' || i.status === 'pending') : null;
  const dur = stepsDuration(items);
  let summary;
  if (live) summary = current ? stepLabel(current) : (tools.length ? 'Working' : 'Thinking');
  else if (!tools.length) summary = dur > 1500 ? `Thought for ${fmtDuration(dur)}` : 'Thought it through';
  else summary = `Worked${dur > 1500 ? ' for ' + fmtDuration(dur) : ''} · ${tools.length} step${tools.length === 1 ? '' : 's'}`;
  return h('details', { className: 'steps' + (live ? ' live' : ''), open: live, key: live ? 'live' : 'done' },
    h('summary', null,
      live ? h(Orb, { state: 'thinking', size: 16 }) : h(Icon, { name: tools.length ? 'todo' : 'thought' }),
      h('span', { className: 'steps-summary' + (live ? ' shimmer' : '') }, summary),
      live && tools.length ? h('span', { className: 'steps-count' }, `${tools.length}`) : null),
    h('ol', { className: 'step-list' }, items.map((it) => h(StepRow, { key: it.key, it, pid, live, onOpenFile }))));
}

// ─── cards that need the user ─────────────────────────────────────────────────
class CallbackCard extends Component {
  constructor(p) { super(p); this.state = { text: '' }; this.ref = null; }
  componentDidMount() { if (!this.props.it.answered && this.ref) this.ref.focus({ preventScroll: true }); }
  render({ it, onAnswer }, { text }) {
    const d = it.detail || {};
    const answer = (payload) => onAnswer && onAnswer(it.callback_id, payload);
    if (it.kind === 'approval') {
      const what = d.command || (d.input && (d.input.command || d.input.filePath || d.input.path)) || d.title || d.tool || d.toolName || 'a tool';
      return h('div', { className: 'card-cb' + (it.answered ? ' done' : ''), tabIndex: -1, ref: (el) => { this.ref = el; } },
        h('div', { className: 'cb-title' }, h(Icon, { name: 'shell' }), ' The agent wants to run'),
        h('pre', { className: 'cb-what' }, String(what)),
        it.answered ? h('div', { className: 'cb-done' }, 'Answered') : h('div', { className: 'cb-actions' },
          h('button', { type: 'button', className: 'btn primary', onClick: () => answer({ decision: 'approve_once' }) }, 'Allow once'),
          h('button', { type: 'button', className: 'btn', onClick: () => answer({ decision: 'approve_for_session' }) }, 'Always in this chat'),
          h('button', { type: 'button', className: 'btn ghost', onClick: () => answer({ decision: 'reject_once' }) }, 'Deny')));
    }
    const qs = Array.isArray(d.questions) ? d.questions : null;
    const question = (qs && qs[0] && (qs[0].question || qs[0].prompt)) || d.prompt || d.message || d.title || 'The agent has a question';
    const options = (qs && qs[0] && qs[0].options) || d.options || [];
    return h('div', { className: 'card-cb' + (it.answered ? ' done' : ''), tabIndex: -1, ref: (el) => { this.ref = el; } },
      h('div', { className: 'cb-title' }, h(Icon, { name: 'question' }), ' ', question),
      it.answered ? h('div', { className: 'cb-done' }, 'Answered') : h(Fragment, null,
        options.length ? h('div', { className: 'cb-actions' }, options.map((o, i) => {
          const label = typeof o === 'string' ? o : (o.label || o.value || JSON.stringify(o));
          const value = typeof o === 'string' ? o : (o.value || o.label);
          return h('button', { key: i, type: 'button', className: 'btn', onClick: () => answer({ answers: [value] }) }, label);
        })) : null,
        h('form', { className: 'cb-free', onSubmit: (e) => { e.preventDefault(); if (text.trim()) answer({ answers: [text.trim()] }); } },
          h('input', { value: text, placeholder: options.length ? 'Or type your own answer' : 'Type your answer',
            onInput: (e) => this.setState({ text: e.target.value }), 'aria-label': 'Your answer' }),
          h('button', { type: 'submit', className: 'btn primary' }, 'Send'))));
  }
}

// ─── a turn ───────────────────────────────────────────────────────────────────
function Attachments({ list, pid, onOpenFile }) {
  if (!list || !list.length) return null;
  return h('div', { className: 'att-row' }, list.map((a) => {
    const img = (a.type || '').startsWith('image/');
    return h('button', { key: a.path, type: 'button', className: 'att' + (img ? ' att-img' : ''),
      onClick: () => onOpenFile && onOpenFile(a.path), title: a.path },
      img ? h('img', { src: rawUrl(pid, a.path), alt: a.name, loading: 'lazy' })
        : h(Fragment, null, h(Icon, { name: fileIcon(a.path, a.type), size: 18 }),
          h('span', { className: 'att-text' }, h('b', null, a.name), h('small', null, fmtSize(a.size)))));
  }));
}

function Turn({ turn, pid, live, phase, phaseElapsed, busy, onAnswer, onOpenFile, onRetry }) {
  const segs = toSegments(turn.agent);
  const lastTextIdx = (() => { for (let i = segs.length - 1; i >= 0; i--) if (segs[i].type === 'assistant') return i; return -1; })();
  const hasOutput = turn.agent.some((i) => i.role !== 'system');
  const streamingText = live && lastTextIdx === segs.length - 1 && lastTextIdx >= 0;
  const answerText = turn.agent.filter((i) => i.role === 'assistant').map((i) => i.content).join('\n\n');
  return h('section', { className: 'turn' },
    turn.user ? h('div', { className: 'msg-user' },
      h('div', { className: 'bubble' }, turn.user.content),
      h(Attachments, { list: turn.user.attachments, pid, onOpenFile })) : null,
    h('div', { className: 'msg-agent' },
      segs.map((s, i) => {
        if (s.type === 'steps') {
          const groupLive = live && i === segs.length - 1;
          return h(StepsGroup, { key: s.key + (groupLive ? '-live' : '-done'), items: s.items, pid, live: groupLive, onOpenFile });
        }
        if (s.type === 'assistant') {
          return h('div', { key: s.key, className: 'agent-text' },
            h(Rich, { source: s.item.content, pid, streaming: live && i === lastTextIdx && streamingText, onOpenFile }));
        }
        if (s.type === 'callback') return h(CallbackCard, { key: s.key, it: s.item, onAnswer });
        if (s.type === 'error') return h('div', { key: s.key, className: 'note err' }, s.item.content);
        return h('div', { key: s.key, className: 'note' }, s.item.content);
      }),
      live && !hasOutput ? h(PhaseLine, { phase: phase || 'preparing', elapsed: phaseElapsed }) : null,
      live && hasOutput && !streamingText && !(segs.length && segs[segs.length - 1].type === 'steps')
        ? h('div', { className: 'working' }, h(Orb, { state: 'thinking', size: 16 }), h('span', { className: 'shimmer' }, 'Working…')) : null,
      !live && answerText ? h('div', { className: 'msg-actions' },
        h('button', { type: 'button', className: 'icon-btn', 'aria-label': 'Copy answer', title: 'Copy',
          onClick: (e) => copyText(answerText, null) }, h(Icon, { name: 'copy' })),
        turn.user && !busy ? h('button', { type: 'button', className: 'icon-btn', 'aria-label': 'Ask again', title: 'Ask again',
          onClick: () => onRetry && onRetry(turn.user) }, h(Icon, { name: 'retry' })) : null) : null));
}

// ─── empty state ──────────────────────────────────────────────────────────────
const SUGGESTIONS = [
  ['What\'s in my workspace?', 'Look around my workspace and tell me in a few lines what is set up and running.'],
  ['Add a page to my app', 'Add a simple "About" page to the app running in my workspace and give me its URL.'],
  ['Summarise a document', 'I will upload a PDF — read it and give me a one-paragraph summary plus the key figures as a table.'],
  ['Chart some data', 'Make a small CSV of monthly sales for a year, then draw it as a line chart and show it here.'],
];

export function EmptyState({ title, onSuggest }) {
  return h('div', { className: 'empty' },
    h(Orb, { state: 'idle', size: 64 }),
    h('h2', null, title || 'What should we work on?'),
    h('p', null, 'Everything happens in your own workspace — files, apps and results stay there.'),
    h('div', { className: 'chips' }, SUGGESTIONS.map(([label, text]) =>
      h('button', { key: label, type: 'button', className: 'chip', onClick: () => onSuggest && onSuggest(text) }, label))));
}

// ─── the scroller ─────────────────────────────────────────────────────────────
export class Feed extends Component {
  constructor(p) {
    super(p);
    this.state = { stuck: true };
    this.scroller = null; this.inner = null; this.ro = null;
    this.lastTop = 0;
    this.onScroll = () => {
      // Content growing under the reader fires scroll events too, so position alone
      // cannot tell "the user scrolled away" from "new output arrived". Only a move
      // upward unpins; getting back to the bottom pins again.
      const el = this.scroller; if (!el) return;
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
      let stuck = this.state.stuck;
      if (atBottom) stuck = true;
      else if (el.scrollTop < this.lastTop - 2) stuck = false;
      this.lastTop = el.scrollTop;
      if (stuck !== this.state.stuck) this.setState({ stuck });
    };
    this.jump = () => { const el = this.scroller; if (el) { el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); this.setState({ stuck: true }); } };
  }

  componentDidMount() {
    this.ro = new ResizeObserver(() => { if (this.state.stuck && this.scroller) this.scroller.scrollTop = this.scroller.scrollHeight; });
    if (this.inner) this.ro.observe(this.inner);
    if (this.scroller) this.scroller.scrollTop = this.scroller.scrollHeight;
  }

  componentDidUpdate(prev) {
    // a project switch or a message the user just sent always goes to the bottom
    if (prev.pid !== this.props.pid || (this.props.items.length > prev.items.length
        && this.props.items[this.props.items.length - 1]?.role === 'user')) {
      if (this.scroller) this.scroller.scrollTop = this.scroller.scrollHeight;
      if (!this.state.stuck) this.setState({ stuck: true });
    }
  }

  componentWillUnmount() { if (this.ro) this.ro.disconnect(); }

  render(p, { stuck }) {
    const turns = toTurns(p.items);
    const liveRun = p.busy ? p.liveRun : null;
    return h('div', { className: 'feed-wrap' },
      h('div', { className: 'feed', ref: (el) => { this.scroller = el; }, onScroll: this.onScroll },
        h('div', { className: 'feed-inner', role: 'log', 'aria-busy': p.busy ? 'true' : 'false', 'aria-live': 'off',
          ref: (el) => { this.inner = el; } },
          !turns.length && !p.busy ? h(EmptyState, { title: p.emptyTitle, onSuggest: p.onSuggest }) : null,
          turns.map((t, i) => {
            const isLast = i === turns.length - 1;
            const live = !!p.busy && isLast && (liveRun == null || t.run == null || t.run === liveRun);
            return h(Turn, { key: t.key, turn: t, pid: p.pid, live, phase: p.phase, phaseElapsed: p.phaseElapsed,
              busy: p.busy, onAnswer: p.onAnswer, onOpenFile: p.onOpenFile, onRetry: p.onRetry });
          }))),
      !stuck ? h('button', { type: 'button', className: 'jump', onClick: this.jump, 'aria-label': 'Jump to latest' },
        h(Icon, { name: 'down' }), p.busy ? ' New output' : ' Latest') : null,
      h('div', { className: 'sr-only', role: 'status', 'aria-live': 'polite' }, p.announce || ''));
  }
}
